// Developed by Softvative.com
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MetricCard from './MetricCard';
import PerformanceChart from './PerformanceChart';
import RecentIncidents from './RecentIncidents';
import UpcomingChanges from './UpcomingChanges';
import { useIncidents } from '../../context/IncidentContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const {
    FiTrendingUp,
    FiAlertTriangle,
    FiCheckCircle,
    FiClock,
    FiZap,
    FiUsers,
    FiServer,
    FiActivity,
    FiShield,
} = FiIcons;

const Dashboard = () => {
    const { incidents, getStatistics } = useIncidents();
    const { user, isSuperAdmin, isTenantOwner, isTenantUser } = useAuth();
    const stats = getStatistics();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const isDev = import.meta.env.VITE_NODE_ENV === 'development';

    useEffect(() => {
        setLoading(true);
        if (isDev) {
            // Static mock data
            setDashboardData({
                totalTenants: 12,
                totalUsers: 245,
                totalIncidents: 89,
                criticalIncidents: 7,
                systemHealth: 96,

                resolvedToday: 14,
                avgResolutionTime: '2h 30m',
                slaCompliance: 92,

                myIncidents: 5,
                openIncidents: 3,

                tenantMetrics: [
                    {
                        tenantId: 1,
                        name: 'Acme Corp',
                        plan: 'Premium',
                        users: 45,
                        incidents: 12,
                        health: 97,
                        status: 'Active',
                    },
                    {
                        tenantId: 2,
                        name: 'TechSoft',
                        plan: 'Basic',
                        users: 20,
                        incidents: 8,
                        health: 85,
                        status: 'Active',
                    },
                    {
                        tenantId: 3,
                        name: 'Global Inc',
                        plan: 'Enterprise',
                        users: 100,
                        incidents: 25,
                        health: 78,
                        status: 'Inactive',
                    },
                ],

                recentActivity: [
                    {
                        id: 1,
                        title: 'Server downtime',
                        status: 'Open',
                        priority: 'High',
                    },
                    {
                        id: 2,
                        title: 'Login issue',
                        status: 'Resolved',
                        priority: 'Medium',
                    },
                ],
            });
        } else {
            if (!user) {
                navigate('/');
                return;
            }
            fetchDashboardData();
        }
        setLoading(false);
    }, [user]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/dashboard/metrics');

            console.log('Dashboard API Response:', response);
            if (response.success) {
                setDashboardData(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Super Admin Metrics
    const superAdminMetrics = dashboardData
        ? [
            {
                title: 'Total Tenants',
                value: dashboardData.totalTenants,
                icon: FiServer,
                color: 'purple',
            },
            {
                title: 'Total Users',
                value: dashboardData.totalUsers,
                icon: FiUsers,
                color: 'blue',
            },
            {
                title: 'Total Incidents',
                value: dashboardData.totalIncidents,
                icon: FiTrendingUp,
                color: 'indigo',
            },
            {
                title: 'Critical Incidents',
                value: dashboardData.criticalIncidents,
                icon: FiAlertTriangle,
                color: 'red',
            },
            {
                title: 'System Health',
                value: `${dashboardData.systemHealth}%`,
                icon: FiActivity,
                color: 'green',
            },
        ]
        : [];

    // Tenant Owner Metrics
    const tenantOwnerMetrics = dashboardData
        ? [
            {
                title: 'Total Incidents',
                value: dashboardData.totalIncidents,
                icon: FiTrendingUp,
                color: 'blue',
            },
            {
                title: 'Critical Incidents',
                value: dashboardData.criticalIncidents,
                icon: FiAlertTriangle,
                color: 'red',
            },
            {
                title: 'Resolved Today',
                value: dashboardData.resolvedToday,
                icon: FiCheckCircle,
                color: 'green',
            },
            {
                title: 'Avg. Resolution Time',
                value: dashboardData.avgResolutionTime,
                icon: FiClock,
                color: 'purple',
            },
            {
                title: 'SLA Compliance',
                value: `${dashboardData.slaCompliance}%`,
                icon: FiZap,
                color: 'orange',
            },
        ]
        : [];

    // Tenant User Metrics
    const tenantUserMetrics = dashboardData
        ? [
            {
                title: 'My Incidents',
                value: dashboardData.myIncidents,
                icon: FiTrendingUp,
                color: 'blue',
            },
            {
                title: 'Open Incidents',
                value: dashboardData.openIncidents,
                icon: FiAlertTriangle,
                color: 'orange',
            },
            {
                title: 'Resolved Today',
                value: dashboardData.resolvedToday,
                icon: FiCheckCircle,
                color: 'green',
            },
            {
                title: 'Avg. Resolution Time',
                value: dashboardData.avgResolutionTime,
                icon: FiClock,
                color: 'purple',
            },
        ]
        : [];

    // Select metrics based on role
    let metrics = tenantOwnerMetrics;
    if (isSuperAdmin()) {
        metrics = superAdminMetrics;
    } else if (isTenantUser()) {
        metrics = tenantUserMetrics;
    }

    const getGreeting = () => {
        if (!user) return 'Welcome!';
        const firstName = user.firstName || 'there';
        if (isSuperAdmin()) {
            return `Welcome, ${firstName}! System Overview`;
        } else if (isTenantOwner()) {
            return `Welcome back, ${firstName}! Tenant Dashboard`;
        } else {
            return `Welcome back, ${firstName}! Your Dashboard`;
        }
    };

    const getSubtitle = () => {
        if (isSuperAdmin()) {
            return 'Monitor all tenants and system-wide metrics';
        } else if (isTenantOwner()) {
            return "Here's your tenant's service desk overview";
        } else {
            return 'View your assigned incidents and activity';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 h-full overflow-y-auto"
        >
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <div className="flex items-center space-x-3">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {getGreeting()}
                        </h1>
                        {isSuperAdmin() && (
                            <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 text-sm font-medium rounded-full flex items-center space-x-1">
                                <SafeIcon icon={FiShield} className="w-4 h-4" />
                                <span>Super Admin</span>
                            </span>
                        )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">{getSubtitle()}</p>
                </div>
            </div>

            {/* Metrics Grid */}
            <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 ${isSuperAdmin() ? 'lg:grid-cols-5' : isTenantUser() ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
                    }`}
            >
                {metrics.map((metric, index) => (
                    <MetricCard
                        key={index}
                        title={metric.title}
                        value={metric.value}
                        icon={metric.icon}
                        color={metric.color}
                    />
                ))}
            </div>

            {/* Super Admin: Tenant Metrics Table */}
            {isSuperAdmin() && dashboardData?.tenantMetrics && (
                <div className="mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Tenant Metrics
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Tenant
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Plan
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Users
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Incidents
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Health
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {dashboardData.tenantMetrics.map((tenant) => (
                                        <tr
                                            key={tenant.tenantId}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                                {tenant.name}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {tenant.plan}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {tenant.users}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {tenant.incidents}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${tenant.health >= 95
                                                                    ? 'bg-green-500'
                                                                    : tenant.health >= 80
                                                                        ? 'bg-yellow-500'
                                                                        : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${tenant.health}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                        {tenant.health}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span
                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.status === 'Active'
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                        }`}
                                                >
                                                    {tenant.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Performance Chart */}
                <div className="lg:col-span-2">
                    <PerformanceChart />
                </div>

                {/* Recent Incidents */}
                <div>
                    <RecentIncidents
                        incidents={
                            dashboardData?.recentActivity || incidents.slice(0, 5)
                        }
                    />
                </div>

                {/* Upcoming Changes - Static for now */}
                {!isTenantUser() && (
                    <div className="lg:col-span-3">
                        <UpcomingChanges />
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default Dashboard;